import React, { useState, useEffect } from 'react';
import { getFirestore, getDocs, collection, query, where } from 'firebase/firestore';

const SiteMetricsTab = ({ user, location, publicStore }) => {
  const [metrics, setMetrics] = useState({
    totalCategories: 0,
    totalProducts: 0,
    totalOptions: 0,
    avgProductPrice: 0,
    lastSync: null,
    publishedSince: null,
    storeStatus: 'preparing',
    // Online ordering metrics
    onlineOrders: 0,
    onlineRevenue: 0,
    avgOnlineOrder: 0,
    onlineOrdersToday: 0,
    onlineRevenueToday: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publicStore && location) {
      loadMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicStore, location]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const locationId = location.id;

      // Content metrics
      const catsSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories'));
      const categories = catsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      let products = [];
      for (const cat of categories) {
        const prodSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories', cat.id, 'products'));
        prodSnap.docs.forEach((p) => products.push({ ...p.data() }));
      }
      const optsSnap = await getDocs(collection(db, 'publicStores', locationId, 'productOptions'));
      const options = optsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const prices = products.map((p) => typeof p.price === 'number' ? p.price : parseFloat(p.price) || 0);
      const avg = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;

      // Online ordering metrics from sales
      let onlineOrders = 0;
      let onlineRevenue = 0;
      let onlineOrdersToday = 0;
      let onlineRevenueToday = 0;

      try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const salesRef = collection(db, 'users', user.uid, 'locations', locationId, 'sales');
        const onlineQuery = query(salesRef, where('salesChannel', '==', 'online orders'));
        const onlineSnap = await getDocs(onlineQuery);

        onlineSnap.docs.forEach((d) => {
          const s = d.data();
          if (s.status === 'canceled' || s.orderStatus === 'Canceled') return;
          const total = typeof s.total === 'number' ? s.total : parseFloat(s.total) || 0;
          onlineOrders++;
          onlineRevenue += total;

          const orderDate = s.orderDate?.toDate ? s.orderDate.toDate() : (s.createdAt?.toDate ? s.createdAt.toDate() : null);
          if (orderDate && orderDate >= todayStart) {
            onlineOrdersToday++;
            onlineRevenueToday += total;
          }
        });
      } catch (e) {
        console.warn('Error loading online sales metrics:', e);
      }

      const avgOnlineOrder = onlineOrders > 0 ? onlineRevenue / onlineOrders : 0;

      setMetrics({
        totalCategories: categories.length,
        totalProducts: products.length,
        totalOptions: options.length,
        avgProductPrice: avg,
        lastSync: publicStore.menuSyncedAt,
        publishedSince: publicStore.createdAt,
        storeStatus: publicStore.status || 'preparing',
        onlineOrders,
        onlineRevenue,
        avgOnlineOrder,
        onlineOrdersToday,
        onlineRevenueToday,
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => `$${(typeof val === 'number' ? val : parseFloat(val) || 0).toFixed(2)}`;

  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    try {
      return ts.toDate ? ts.toDate().toLocaleString() : new Date(ts.seconds * 1000).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  if (!publicStore) {
    return (
      <div className="alert alert-warning">
        Please deploy your store in the <strong>Site Information</strong> tab to view metrics.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-orange" role="status"><span className="sr-only">Loading...</span></div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="font-weight-bold mb-1">
            <i className="fas fa-chart-line text-primary mr-2"></i>
            Site Engagement & Metrics
          </h4>
          <p className="text-muted mb-0">
            Overview of your public store performance and content health.
          </p>
        </div>
        <span className={`badge badge-${metrics.storeStatus === 'published' ? 'success' : 'warning'}`} style={{ fontSize: '0.95rem', padding: '8px 14px' }}>
          {metrics.storeStatus.toUpperCase()}
        </span>
      </div>

      {/* Online Ordering Metrics */}
      <div className="row mb-4">
        <div className="col-12">
          <h5 className="font-weight-bold mb-3">
            <i className="fas fa-shopping-cart text-primary mr-2"></i>
            Online Ordering Performance
          </h5>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-primary">
            <div className="inner">
              <h3>{metrics.onlineOrders}</h3>
              <p>Total Online Orders</p>
            </div>
            <div className="icon"><i className="fas fa-shopping-bag" /></div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-success">
            <div className="inner">
              <h3>{formatCurrency(metrics.onlineRevenue)}</h3>
              <p>Online Revenue</p>
            </div>
            <div className="icon"><i className="fas fa-dollar-sign" /></div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-info">
            <div className="inner">
              <h3>{formatCurrency(metrics.avgOnlineOrder)}</h3>
              <p>Avg. Online Order</p>
            </div>
            <div className="icon"><i className="fas fa-receipt" /></div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-warning">
            <div className="inner">
              <h3>{metrics.onlineOrdersToday}</h3>
              <p>Orders Today</p>
            </div>
            <div className="icon"><i className="fas fa-calendar-day" /></div>
          </div>
        </div>
      </div>

      {/* Content Health */}
      <div className="row">
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-secondary">
            <div className="inner">
              <h3>{metrics.totalCategories}</h3>
              <p>Categories</p>
            </div>
            <div className="icon"><i className="fas fa-tags" /></div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-secondary">
            <div className="inner">
              <h3>{metrics.totalProducts}</h3>
              <p>Products</p>
            </div>
            <div className="icon"><i className="fas fa-utensils" /></div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-secondary">
            <div className="inner">
              <h3>{metrics.totalOptions}</h3>
              <p>Option Sets</p>
            </div>
            <div className="icon"><i className="fas fa-list-ul" /></div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="small-box bg-secondary">
            <div className="inner">
              <h3>{formatCurrency(metrics.avgProductPrice)}</h3>
              <p>Avg. Product Price</p>
            </div>
            <div className="icon"><i className="fas fa-dollar-sign" /></div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <div className="card card-outline card-primary">
            <div className="card-header">
              <h3 className="card-title"><i className="fas fa-heartbeat mr-2"></i>Store Health</h3>
            </div>
            <div className="card-body">
              <ul className="list-group list-group-flush">
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <span><i className="fas fa-sync-alt mr-2 text-info"></i>Last Menu Sync</span>
                  <span className="badge badge-light">{formatDate(metrics.lastSync)}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <span><i className="fas fa-calendar-alt mr-2 text-success"></i>Store Created</span>
                  <span className="badge badge-light">{formatDate(metrics.publishedSince)}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <span><i className="fas fa-globe mr-2 text-primary"></i>Public URL</span>
                  <span className="badge badge-light">
                    {publicStore.published ? (
                      <span className="text-success">Live</span>
                    ) : (
                      <span className="text-muted">Not published</span>
                    )}
                  </span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <span><i className="fas fa-palette mr-2 text-warning"></i>Design Template</span>
                  <span className="badge badge-light">{publicStore.design?.template || 'modern'}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card card-outline card-success">
            <div className="card-header">
              <h3 className="card-title"><i className="fas fa-bullhorn mr-2"></i>Today's Online Revenue</h3>
            </div>
            <div className="card-body text-center" style={{ padding: 40 }}>
              <h2 style={{ color: '#ff6b35', fontWeight: 700 }}>
                {formatCurrency(metrics.onlineRevenueToday)}
              </h2>
              <p className="text-muted">
                {metrics.onlineOrdersToday} order{metrics.onlineOrdersToday === 1 ? '' : 's'} placed today
              </p>
              {metrics.onlineOrders === 0 && (
                <div className="mt-3 text-muted small">
                  <i className="fas fa-info-circle mr-1"></i>
                  Once customers start placing orders, metrics will appear here automatically.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteMetricsTab;
